import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../store/store";
import { registerUser } from "../store/slice/authThunks";
import ProviderRegistration from "./ProviderRegistration";

const UserSignUp: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, user } = useSelector(
    (state: RootState) => state.auth
  );

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<"ROLE_USER" | "ROLE_WORKMAN">(
    "ROLE_USER"
  );

  // Track whether handyman registration is completed
  const [handymanCompleted, setHandymanCompleted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      alert("Passwords do not match");
      return;
    }

    // If user is handyman and not completed provider registration, prevent submission
    if (role === "ROLE_WORKMAN" && !handymanCompleted) {
      alert("Please complete the provider registration below.");
      return;
    }

    dispatch(registerUser({ email, password, phone, roles: [role] }) as any);
  };

  return (
    <section className="bg-gray-50 dark:bg-gray-900 min-h-full flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md bg-white rounded-lg shadow dark:bg-gray-800 dark:border dark:border-gray-700 overflow-y-auto max-h-[95vh]">
        <div className="p-6 sm:p-8 space-y-6">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white text-center">
            Create an account
          </h1>

          {error && <p className="text-red-600 text-center">{error}</p>}
          {user && (
            <p className="text-green-600 text-center">
              Registered successfully!
            </p>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            {/* Email */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                Your email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="name@company.com"
                className="w-full p-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 text-gray-900 focus:ring-green-600 focus:border-green-600 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              />
            </div>

            {/* Password */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full p-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 text-gray-900 focus:ring-green-600 focus:border-green-600 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              />
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                Confirm password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full p-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 text-gray-900 focus:ring-green-600 focus:border-green-600 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <label className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                Phone
              </label>
              <input
                type="text"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="08012345678"
                className="w-full p-2.5 text-sm rounded-lg border border-gray-300 bg-gray-50 text-gray-900 focus:ring-green-600 focus:border-green-600 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              />
            </div>

            {/* Roles */}
            <div>
              <span className="block mb-1 text-sm font-medium text-gray-900 dark:text-white">
                Select Role
              </span>
              <div className="flex gap-4">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="role"
                    value="ROLE_USER"
                    checked={role === "ROLE_USER"}
                    onChange={() => setRole("ROLE_USER")}
                    className="w-4 h-4 text-green-600 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-green-600"
                  />
                  <span className="ml-2 text-gray-900 dark:text-white">
                    Customer
                  </span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="role"
                    value="ROLE_WORKMAN"
                    checked={role === "ROLE_WORKMAN"}
                    onChange={() => setRole("ROLE_WORKMAN")}
                    className="w-4 h-4 text-green-600 bg-white border border-gray-300 rounded focus:ring-2 focus:ring-green-600"
                  />
                  <span className="ml-2 text-gray-900 dark:text-white">
                    Handyman
                  </span>
                </label>
              </div>
            </div>

            {/* Terms */}
            <div className="flex items-start">
              <input
                id="terms"
                type="checkbox"
                required
                className="w-4 h-4 rounded border border-gray-300 bg-white checked:bg-green-600 checked:ring-2 checked:ring-green-600 focus:outline-none focus:ring-2 focus:ring-green-300 dark:bg-gray-700 dark:border-gray-600"
              />
              <label
                htmlFor="terms"
                className="ml-2 text-sm text-gray-500 dark:text-gray-300"
              >
                By clicking <strong>Create Account</strong>, you agree to our{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:underline dark:text-green-400"
                >
                  Terms of Use
                </a>{" "}
                and{" "}
                <a
                  href="/privacy-policy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-green-600 hover:underline dark:text-green-400"
                >
                  Privacy Policy
                </a>
                .
              </label>
            </div>

            {/* Submit */}
            <button
              type="submit"
              className={`w-full py-2.5 px-5 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-4 ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 focus:ring-green-300 dark:bg-green-600 dark:hover:bg-green-700 dark:focus:ring-green-800"
              }`}
              disabled={loading}
            >
              {loading ? "Creating..." : "Create an account"}
            </button>
          </form>

          {/* --- HANDYMAN PROVIDER REGISTRATION --- */}
          {role === "ROLE_WORKMAN" && (
            <ProviderRegistration
              onComplete={() => setHandymanCompleted(true)}
            />
          )}

          {/* Phone Sign Up Link */}
          <p className="text-sm text-center text-gray-500 dark:text-gray-400">
            Prefer to sign up with your phone number instead?{" "}
            <a
              href="/login-phone"
              className="font-medium text-green-600 hover:underline dark:text-green-400"
            >
              Sign up with phone
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default UserSignUp;
